import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { ProductCard } from "@/components/ProductCard";
import { getProducts } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";

const FILTERS = [
    { key: "all", label: "All" },
    { key: "cut", label: "Cuts" },
    { key: "gift", label: "Gift Boxes" },
    { key: "bundle", label: "Bundles" },
    { key: "accessory", label: "Accessories" },
];

export default function Shop() {
    const [products, setProducts] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const filter = searchParams.get("category") || "all";

    useEffect(() => {
        getProducts().then(setProducts).catch(() => setProducts([]));
    }, []);

    const filtered = useMemo(() => {
        if (!products) return [];
        if (filter === "all") return products;
        return products.filter((p) => p.category === filter);
    }, [products, filter]);

    return (
        <main className="bg-[#F6F1E7] min-h-screen pt-28 sm:pt-36 pb-24" data-testid="shop-page">
            <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
                <Reveal className="text-center mb-12">
                    <p className="eyebrow text-[#8C6A3D] mb-4">The Collection</p>
                    <h1 className="font-serif-display text-5xl sm:text-6xl font-medium text-[#0B0B0C]">Shop</h1>
                    <p className="mt-4 text-sm text-black/50 max-w-md mx-auto">
                        Premium British dried beef. Two ingredients, five farms, full traceability.
                    </p>
                </Reveal>

                <div className="flex justify-center gap-2 sm:gap-3 mb-14 flex-wrap" data-testid="shop-filters">
                    {FILTERS.map((f) => (
                        <button
                            key={f.key}
                            onClick={() => setSearchParams(f.key === "all" ? {} : { category: f.key })}
                            className={`eyebrow px-5 py-2.5 border rounded-full transition-colors duration-200 ${
                                filter === f.key
                                    ? "bg-[#0F2A1F] text-[#F6F1E7] border-[#0F2A1F]"
                                    : "border-black/15 text-black/60 hover:border-[#0F2A1F] hover:text-[#0F2A1F]"
                            }`}
                            data-testid={`shop-filter-${f.key}`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>

                {!products ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[...Array(6)].map((_, i) => (
                            <Skeleton key={i} className="h-96 rounded-xl bg-black/[0.06]" />
                        ))}
                    </div>
                ) : filtered.length === 0 ? (
                    <p className="text-center text-black/50 py-20" data-testid="shop-empty-state">
                        Nothing here just yet.
                    </p>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filtered.map((product, i) => (
                            <Reveal key={product.id} delay={Math.min(i * 0.06, 0.3)}>
                                <ProductCard product={product} />
                            </Reveal>
                        ))}
                    </div>
                )}
            </div>
        </main>
    );
}
